import React, { useState, forwardRef, useRef } from "react";
import DatePicker from "react-datepicker";
import FormTextField from "../FormTextField";
import { Button, Typography, withStyles } from "@material-ui/core";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  SkipPrevious,
  SkipNext,
  DateRange,
  Clear,
} from '@material-ui/icons/';
import {
  format,
  getMonth,
  getYear,
  isValid,
  parse,
  isDate,
  isBefore,
} from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./styles";
import clsx from "clsx";
import { func, string, instanceOf, bool, object } from 'prop-types'
import { DATE_FORMAT } from "../../constants";

function DateRangePicker(props) {
  const {
    id='date-range-picker',
    classes,
    startDate,
    onStartDateChange,
    endDate,
    onEndDateChange,
    onClear,
    startDateClass,
    endDateClass,
    isRangePicker=true,
    onDateRangeError,
    startDatePlaceholder,
    endDatePlaceholder,
    containerClass,
    startDateTextFieldClass,
    endDateTextFieldClass,
    startDateFieldContainerClass,
    endDateFieldContainerClass,
  } = props;

  const [startDateInputValue, setStartDateInputValue] = useState(
    startDate ? format(startDate, DATE_FORMAT) : null,
  );
  const [endDateInputValue, setEndDateInputValue] = useState(
    endDate ? format(endDate, DATE_FORMAT) : null,
  );
  const [selectedYear, setSelectedYear] = useState(getYear(startDate));
  const [selectedMonth, setSelectedMonth] = useState(getMonth(startDate));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  /* flag to track date picker's state changed using setSelected method*/
  const isSetSelected = useRef(false);

  /* refs to control the internal state of date pickers when desired props are not exposed */
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  /* onChange handler when typing start date */
  const onChangeStartDateInput = (value = '', isSubmit = false) => {
    const startDateObj = parseDate(value);
    const endDateObj = parseDate(endDateInputValue);
    startDateObj && refreshCalendar(startDateRef, startDateObj);
    setStartDateInputValue(value);
    if(isSubmit && isRangeValid(startDateObj, endDateObj)) {
      onStartDateChange(startDateObj);
    }
  }

  /* onBlur handler for start and end date picker */
  const onBlurHandler = (date = '', isStartDatePicker = true) => {
    if(isCalendarOpen) return;
    const startDateObj = parseDate(isStartDatePicker ? date : startDateInputValue);
    const endDateObj = parseDate(!isStartDatePicker ? date : endDateInputValue);
    if(isRangeValid(startDateObj, endDateObj)) {
      isStartDatePicker ? onStartDateChange(startDateObj) : onEndDateChange(endDateObj);
    }
  }

  /* Called when a day is selected from start date picker's calendar.
     When refreshing the calendar using setSelected method, side effect
     causes onChange of date picker to trigger undesirably. We use this
     flag to prevent performing any steps when this happens. */
  const onSelectStartDate = (date = new Date()) => {
    if (isSetSelected.current) {
      isSetSelected.current = false;
      return;
    }
    setStartDateInputValue(format(date, DATE_FORMAT));
    if(!isRangeValid(date, parseDate(endDateInputValue, true))) return;
    onStartDateChange(new Date(date.setHours(0,0,0,0)));
  }

  /* onChange handler when typing end date */
  const onChangeEndDateInput = (date = '', isSubmit = false) => {
    const endDateObj = parseDate(date);
    const startDateObj = parseDate(startDateInputValue);
    endDateObj && refreshCalendar(endDateRef, endDateObj);
    setEndDateInputValue(date);
    if(isSubmit && isRangeValid(startDateObj, endDateObj)) {
      onEndDateChange(endDateObj);
    }
  }

  /* Called when a day is selected from end date picker's calendar */
  const onSelectEndDate = (date = new Date()) => {
    const endDateObj = new Date(date.setHours(23,59,59,59))
    const endDateStr = format(endDateObj, DATE_FORMAT);
    setEndDateInputValue(endDateStr);
    onEndDateChange(endDateObj);
  }

  /* Checks if the start date is before end date. If either date is missing, returns true */
  const isRangeValid = (startDate, endDate) => {
    if(!isRangePicker) return true;
    if(!(startDate && endDate)) return true;
    if(isBefore(startDate, endDate)) return true;
    onDateRangeError();
    return false;
  }

  /* Creating a valid date object from string  */
  const parseDate = (date = '', isEndDate = false) => {
    if(!date) return undefined;
    if (date.length !== DATE_FORMAT.length) return undefined;
    const dateInstance = new Date();
    isEndDate ? dateInstance.setHours(0,0,0,0) : dateInstance.setHours(23,59,59,59)
    const parsed = parse(`${date}`, DATE_FORMAT, dateInstance)
    if (isDate(parsed) && isValid(parsed)) return parsed;
    return undefined;
  };

  /* Refreshing the calendar when user types in a new valid date */
  const refreshCalendar = (ref, date) => {
    isSetSelected.current = true;
    ref.current?.setSelected(date);
  }

  /* Custom header for calendar popper */
  const renderCustomHeader = (props) =>
    <CustomHeader
      {...props}
      id={id}
      classes={classes}
      selectedMonth={selectedMonth}
      setSelectedMonth={setSelectedMonth}
      selectedYear={selectedYear}
      setSelectedYear={setSelectedYear}
    />

  return (
    <div id={id} data-apim-test={id} className={clsx(classes.container, containerClass)}>
      <div
        id={`${id}-start-date-picker-container`}
        data-apim-test={`${id}-start-date-picker-container`}
        className={clsx(classes.reactDatePickerBase, startDateClass)}
      >
        <DatePicker
          id={`${id}-start-date-picker`}
          ref={startDateRef}
          selected={parseDate(startDateInputValue)}
          onChange={onSelectStartDate}
          customInput={
            <CustomInput
              inputValue={startDateInputValue}
              handleChange={onChangeStartDateInput}
              handleBlur={value => onBlurHandler(value, true)}
              textFieldClass={clsx(classes.borderRightNoRadius, startDateTextFieldClass)}
              fieldContainerClass={clsx(classes.customInputClass, startDateFieldContainerClass)}
              placeholderText={startDatePlaceholder}
              inputId={`${id}-start-date-field`}
            />
          }
          renderCustomHeader={renderCustomHeader}
          onCalendarOpen={()=> setIsCalendarOpen(true)}
          onCalendarClose={() => setIsCalendarOpen(false)}
          filterDate={date => {
            if(isRangePicker && endDateInputValue) {
              return !isBefore(parseDate(endDateInputValue), date)
            }
            return true;
          }}
          disabledKeyboardNavigation
        />
      </div>
      {
        isRangePicker &&
        <>
          <div
            id={`${id}-label-between-ranges`}
            data-apim-test={`${id}-label-between-ranges`}
            className={classes.labelBetweenRanges}>
            <Typography>
              to
            </Typography>
          </div>
          <div
            id={`${id}-end-date-picker-container`}
            data-apim-test={`${id}-end-date-picker-container`}
            className={clsx(classes.reactDatePickerBase, endDateClass)}
          >
            <DatePicker
              id={`${id}-end-date-picker`}
              ref={endDateRef}
              selected={parseDate(endDateInputValue)}
              startDate={parseDate(startDateInputValue)}
              onChange={onSelectEndDate}
              minDate={startDate}
              customInput={
                <CustomInput
                  inputValue={endDateInputValue}
                  handleChange={onChangeEndDateInput}
                  handleBlur={value => onBlurHandler(value, false)}
                  textFieldClass={clsx(
                    classes.borderLeftNoRadius,
                    classes.borderRightNoRadius,
                    endDateTextFieldClass,
                  )}
                  placeholderText={endDatePlaceholder}
                  inputId={`${id}-end-date-field`}
                  fieldContainerClass={clsx(classes.customInputClass, endDateFieldContainerClass)}
                />
              }
              renderCustomHeader={renderCustomHeader}
              onCalendarOpen={()=> setIsCalendarOpen(true)}
              onCalendarClose={() => setIsCalendarOpen(false)}
              disabledKeyboardNavigation
            />
          </div>
        </>
      }
      <Button
        id={`${id}-clear-button`}
        variant="outlined"
        size="small"
        color="primary"
        onClick={onClear}
        className={classes.clearButton}
      >
        <Clear color={"primary"}/>
      </Button>
    </div>
  );
}

const CustomHeader = (props) => {
  const {
    id,
    date,
    decreaseMonth,
    increaseMonth,
    increaseYear,
    decreaseYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    prevYearButtonDisabled,
    nextYearButtonDisabled,
    classes,
  } = props;

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul",
    "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <div
      id={`${id}-calendar-header`}
      data-apim-test={`${id}-calendar-header`}
      className={classes.headerContainer}
    >
      <Button
        variant="outlined"
        size="small"
        color="primary"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className={classes.navigationButton}
        id={`${id}-calendar-header-decrease-month-button`}
      >
        <KeyboardArrowLeft color={"primary"}/>
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className={classes.navigationButton}
        id={`${id}-calendar-header-increase-month-button`}
      >
        <KeyboardArrowRight color={"primary"}/>
      </Button>
      <Typography id={`${id}-calendar-header-title`} className={classes.headerTitle}>
        {`${months[getMonth(date)]} ${getYear(date)}`}
      </Typography>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        onClick={decreaseYear}
        disabled={prevYearButtonDisabled}
        className={classes.navigationButton}
        id={`${id}-calendar-header-decrease-year-button`}
      >
        <SkipPrevious color={"primary"}/>
      </Button>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        onClick={increaseYear}
        disabled={nextYearButtonDisabled}
        className={classes.navigationButton}
        id={`${id}-header-increase-year-button`}
      >
        <SkipNext color={"primary"}/>
      </Button>
    </div>
  )
}

const CustomInput = forwardRef((props, ref) => {
  const {
    onClick,
    inputValue,
    handleChange,
    handleBlur,
    textFieldClass,
    placeholderText,
    inputId,
    fieldContainerClass,
  } = props;
  return ( <FormTextField
    onClickInput={onClick}
    fieldContainerClass={fieldContainerClass}
    textFieldClass={textFieldClass}
    value={inputValue}
    handleChange={handleChange}
    handleBlur={handleBlur}
    inputRef={ref}
    onKeyPress={e => e.key === 'Enter' && handleChange(e.target.value, true)}
    suffixIcon={<DateRange color={"primary"} onClick={onClick}/>}
    placeholder={placeholderText}
    id={inputId}
  />)
})

DateRangePicker.propTypes = {
  id : string,
  classes : object,
  startDate : instanceOf(Date),
  endDate : instanceOf(Date),
  onStartDateChange : func,
  onEndDateChange : func,
  startDateClass : string,
  endDateClass : string,
  isRangePicker : bool,
  onClear : func,
  notifyMessages : func,
  startDatePlaceholder : string,
  endDatePlaceholder : string,
  onDateRangeError : func,
  containerClass : string,
  startDateTextFieldClass : string,
  endDateTextFieldClass : string,
  startDateFieldContainerClass : string,
  endDateFieldContainerClass : string,
}

CustomHeader.propTypes = {
  id : string,
  date : instanceOf(Date),
  decreaseMonth : func,
  increaseMonth : func,
  increaseYear : func,
  decreaseYear : func,
  prevMonthButtonDisabled : bool,
  nextMonthButtonDisabled : bool,
  prevYearButtonDisabled : bool,
  nextYearButtonDisabled : bool,
  classes : object,
}

CustomInput.propTypes = {
  onClick : func,
  inputValue : string,
  handleChange : func,
  handleBlur : func,
  textFieldClass : string,
  placeholderText : string,
  inputId : string,
  fieldContainerClass : string,
}

export default withStyles(styles)(DateRangePicker)
